/**
 * Google Analytics 4 (via Firebase Analytics) for the ClubEzy auth gateway
 * (sign-in, public registration, email action links).
 *
 * Runs only where the environment's Firebase config has a `measurementId`,
 * which is production alone. Every other environment — and the SSR server —
 * gets a silent no-op, so dev and staging never pollute the prod property.
 * Add a measurementId to another environment only with its own GA stream.
 *
 * Page views are sent by hand rather than by gtag's automatic tracking, so
 * `page_location` can be scrubbed first (see ./analytics-url). The same scrubbed
 * URL is set as a default parameter so every other event, including GA's own
 * enhanced-measurement ones, carries it instead of the raw address bar.
 *
 * Scrubbing matters most here: this app handles the Firebase email action
 * links, whose `oobCode` is a one-time credential.
 *
 * Google signals and ad personalisation are off.
 */
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import {
  Analytics,
  initializeAnalytics,
  isSupported,
  logEvent as fbLogEvent,
  setDefaultEventParameters,
  setUserId as fbSetUserId,
} from 'firebase/analytics';
import { FIREBASE_APP } from '@/app/core/firebase';
import { environment } from '@/environments/environment';
import { sanitiseAnalyticsUrl } from './analytics-url';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly app = inject(FIREBASE_APP);
  private readonly router = inject(Router);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Resolves to null whenever analytics is off or unsupported. */
  private analytics: Promise<Analytics | null> = Promise.resolve(null);
  private started = false;

  /**
   * Initialise GA and start sending page views on every navigation. Call once
   * from the root component. Safe to call anywhere: it does nothing without a
   * configured measurementId or outside the browser.
   */
  start(): void {
    if (this.started || !this.isBrowser || !('measurementId' in environment.firebase)) {
      return;
    }
    this.started = true;

    this.analytics = isSupported()
      .then((supported) => {
        if (!supported) return null;
        const pageLocation = sanitiseAnalyticsUrl(window.location.href);
        setDefaultEventParameters({ page_location: pageLocation });
        return initializeAnalytics(this.app, {
          config: {
            send_page_view: false,
            page_location: pageLocation,
            allow_google_signals: false,
            allow_ad_personalization_signals: false,
          },
        });
      })
      .catch((e) => {
        console.warn('AnalyticsService: init failed', e);
        return null;
      });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.logPageView());
    // The first navigation can finish before the root component starts us.
    if (this.router.navigated) this.logPageView();
  }

  /** Log a custom event. */
  logEvent(eventName: string, params?: Record<string, unknown>): void {
    void this.analytics.then((analytics) => {
      if (!analytics) return;
      try {
        // Cast to bypass the CustomEventName<T> constraint which
        // maps known event names to `never` when called with a plain `string`.
        (fbLogEvent as (...args: unknown[]) => void)(analytics, eventName, params);
      } catch (e) {
        console.warn('AnalyticsService.logEvent error:', e);
      }
    });
  }

  /** Set (or clear, with null) the signed-in user's Firebase uid. */
  setUserId(userId: string | null): void {
    void this.analytics.then((analytics) => {
      if (!analytics) return;
      try {
        fbSetUserId(analytics, userId);
      } catch (e) {
        console.warn('AnalyticsService.setUserId error:', e);
      }
    });
  }

  /** Log a standard login event. */
  logLogin(method?: string): void {
    this.logEvent('login', { method: method ?? 'email' });
  }

  /** Log a standard sign-up event. */
  logSignUp(method?: string): void {
    this.logEvent('sign_up', { method: method ?? 'email' });
  }

  private logPageView(): void {
    const pageLocation = sanitiseAnalyticsUrl(window.location.href);
    void this.analytics.then((analytics) => {
      if (!analytics) return;
      try {
        setDefaultEventParameters({ page_location: pageLocation });
        fbLogEvent(analytics, 'page_view', {
          page_location: pageLocation,
          page_title: document.title,
        });
      } catch (e) {
        console.warn('AnalyticsService.logPageView error:', e);
      }
    });
  }
}
